import { CustomCalendarType } from "@ac-expo/api-types";
import { getFrenchCroppedDayName, getFrenchMonthName } from "@ac-expo/utils";
import frLocale from "@fullcalendar/core/locales/fr";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import timelinePlugin from "@fullcalendar/timeline";
import { parse } from "date-fns";
import dayjs from "dayjs";
import {
  CSSProperties,
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { cn } from "../../../utils";
import { CaretLeftIcon, CaretRightIcon } from "../../icons";
import { FiltersBars } from "./filter-bars";
import { getRenderEventContent } from "./render-event";
import {
  getBackgroundContainerStyle,
  getBackgroundOverlayStyle,
  getCalendarView,
  PlanningEvent,
  PlanningEventGroup,
} from "./utils";

const SLOT_HEIGHT_PX = 50;

interface FullCalendarViewProps {
  filteredSlots: PlanningEvent[];
  groupedEvents: (PlanningEvent | PlanningEventGroup)[];
  mergedSlotsForHorizontal: PlanningEvent[];
  groupedPlaces: Record<string, PlanningEvent[]>;
  visibleDays: dayjs.Dayjs[];
  currentDay: dayjs.Dayjs;
  minTime: string | undefined;
  maxTime: string | undefined;
  isFirstDay: boolean;
  isLastDay: boolean;
  handlePrev: () => void;
  handleNext: () => void;
  setPlaceFilter: (place: string | undefined) => void;
  setVisibleDays: (days: dayjs.Dayjs[]) => void;
  calendarRef: MutableRefObject<FullCalendar | null>;
  onOpenActivitiesDetails?: (events: PlanningEvent[]) => void;
  isRegisterEnabled?: boolean;
  customCalendar?: CustomCalendarType;
  backgroundStyleOptions: {
    backgroundPictureUrl?: string;
    backgroundColor?: string;
    opacity?: number;
  };
}

export const FullCalendarView = ({
  filteredSlots,
  groupedEvents,
  mergedSlotsForHorizontal,
  groupedPlaces,
  visibleDays,
  currentDay,
  minTime,
  maxTime,
  isFirstDay,
  isLastDay,
  handlePrev,
  handleNext,
  setPlaceFilter,
  setVisibleDays,
  calendarRef,
  onOpenActivitiesDetails,
  isRegisterEnabled,
  customCalendar,
  backgroundStyleOptions,
}: FullCalendarViewProps) => {
  const isHorizontalMonthly = customCalendar?.model === "horizontalMonthly";
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isHorizontalMonthly) return;
    const container = containerRef.current;
    if (!container) return;

    const removeWatermark = () => {
      const watermark = container.querySelector(".fc-license-message");
      if (watermark) watermark.remove();
    };

    removeWatermark();
    const observer = new MutationObserver(removeWatermark);
    observer.observe(container, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [isHorizontalMonthly]);

  const initialDate = useMemo(() => {
    if (!isHorizontalMonthly || filteredSlots.length === 0)
      return dayjs().toDate();
    const dates = filteredSlots.map((s) => dayjs(s.start));
    return dayjs.min(dates)?.toDate() ?? dayjs().toDate();
  }, [isHorizontalMonthly, filteredSlots]);

  const handleDatesSet = (arg: { start: Date; end: Date }) => {
    const startDate = dayjs(arg.start);
    const endDate = dayjs(arg.end);
    const dayCount = endDate.diff(startDate, "day");
    const days = Array.from({ length: dayCount }, (_, i) =>
      startDate.add(i, "day")
    );
    setVisibleDays(days);
  };

  const displayMonth =
    visibleDays.length > 0
      ? visibleDays[Math.floor(visibleDays.length / 2)]
      : currentDay;

  const monthYear = `${getFrenchMonthName(displayMonth.month()).toUpperCase()} ${displayMonth.year()}`;

  const { slotMaxTime, slotHeight } = useMemo(() => {
    const minHour = minTime
      ? parse(minTime, "HH:mm:ss", new Date()).getHours()
      : 0;
    const baseMaxHour = maxTime
      ? parse(maxTime, "HH:mm:ss", new Date()).getHours()
      : 24;
    // FullCalendar treats slotMaxTime as exclusive, so pad by one hour to keep
    // the last hour label visible and avoid clipping events ending on it.
    const paddedMaxHour = Math.min(baseMaxHour + 1, 24);
    const hourCount = paddedMaxHour - minHour;
    return {
      slotMaxTime: `${String(paddedMaxHour).padStart(2, "0")}:00:00`,
      slotHeight: `${SLOT_HEIGHT_PX * hourCount}px`,
    };
  }, [minTime, maxTime]);

  const calendarWrapperStyle = useMemo<CSSProperties>(
    () => ({ "--fc-slot-calendar-height": slotHeight }) as CSSProperties,
    [slotHeight]
  );

  return (
    <div className="flex h-full flex-col">
      <div className="gap-6 rounded-t-xl px-6 pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {!isHorizontalMonthly && (
              <p className="font-extrabold text-black">{monthYear}</p>
            )}
            <button type="button" onClick={handlePrev} disabled={isFirstDay}>
              <CaretLeftIcon
                className={cn("cursor-pointer text-xl", {
                  "cursor-not-allowed": isFirstDay,
                })}
                width={32}
                height={32}
                fill={isFirstDay ? "#e4e4e4" : "#287BB9"}
              />
            </button>
            <button type="button" onClick={handleNext} disabled={isLastDay}>
              <CaretRightIcon
                className={cn("cursor-pointer text-xl", {
                  "cursor-not-allowed": isLastDay,
                })}
                width={32}
                height={32}
                fill={isLastDay ? "#e4e4e4" : "#287BB9"}
              />
            </button>
          </div>
          <FiltersBars
            slots={filteredSlots}
            onOpenActivitiesDetails={onOpenActivitiesDetails}
            placeNames={Object.keys(groupedPlaces).filter(
              (place) => place.length > 0
            )}
            setPlaceFilter={setPlaceFilter}
          />
        </div>
      </div>

      <div
        ref={isHorizontalMonthly ? containerRef : undefined}
        className={cn(
          "relative mt-4 flex-1",
          isHorizontalMonthly ? "horizontal-calendar" : "vertical-planning-calendar"
        )}
        style={{
          ...getBackgroundContainerStyle(backgroundStyleOptions),
          ...(!isHorizontalMonthly ? calendarWrapperStyle : {}),
        }}
      >
        <div
          className="absolute inset-0 size-full overflow-hidden object-fill"
          style={getBackgroundOverlayStyle(backgroundStyleOptions)}
        />
        {isHorizontalMonthly ? (
          <FullCalendar
            key="horizontal-calendar"
            ref={calendarRef}
            plugins={[timelinePlugin, interactionPlugin]}
            initialView="timelineMonth"
            initialDate={initialDate}
            headerToolbar={false}
            locale={frLocale}
            events={mergedSlotsForHorizontal}
            slotDuration={{ days: 1 }}
            slotLabelFormat={[{ month: "long" }, { day: "numeric" }]}
            slotMinWidth={50}
            height="auto"
            eventContent={getRenderEventContent({
              onOpenActivitiesDetails,
              isRegisterEnabled,
              customCalendar,
            })}
            dayCellClassNames="bg-transparent"
            eventClassNames="bg-transparent border-0"
          />
        ) : (
          <FullCalendar
            ref={calendarRef}
            plugins={[
              timeGridPlugin,
              interactionPlugin,
              listPlugin,
              dayGridPlugin,
            ]}
            initialView={getCalendarView(customCalendar?.model)}
            headerToolbar={false}
            events={
              customCalendar?.model === "verticalDayHour"
                ? groupedEvents
                : filteredSlots
            }
            views={{
              timeGridSevenDay: {
                type: "timeGrid",
                duration: { days: 7 },
                buttonText: "7 day",
              },
              listSevenDay: {
                type: "dayGrid",
                duration: { days: 7 },
                buttonText: "7 day",
              },
            }}
            firstDay={1}
            datesSet={handleDatesSet}
            slotMinTime={minTime}
            slotMaxTime={slotMaxTime}
            allDaySlot={true}
            dayHeaders={true}
            locale={frLocale}
            slotLabelFormat={{
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }}
            dayHeaderFormat={{ weekday: "short", day: "numeric" }}
            dayHeaderContent={(arg) => {
              const day = dayjs(arg.date);
              return (
                <span
                  className={cn(
                    "rounded-xl px-4 py-1",
                    day.isSame(dayjs(), "day") && "bg-secondary-500 text-white"
                  )}
                >
                  {getFrenchCroppedDayName(day.day())} {day.format("DD/MM")}
                </span>
              );
            }}
            slotDuration="01:00:00"
            expandRows={true}
            height="auto"
            dayCellClassNames="bg-transparent"
            eventClassNames="bg-transparent border-0"
            slotLabelContent={(args) => (
              <div className="font-nunito w-[64px] text-center text-sm text-gray-600">
                {args.text}
              </div>
            )}
            eventContent={getRenderEventContent({
              onOpenActivitiesDetails,
              isRegisterEnabled,
              customCalendar,
            })}
            allDayText="Journée entière"
            allDayClassNames="text-end size-full font-bold text-sm"
          />
        )}
      </div>
    </div>
  );
};
